import {
  TARGET,
  TARGET_GROUP,
  targetConfig as sputilsTargetConfig,
  targetGroupLabels,
} from '@decodo/utils';

import { currentDashboardSupportedTargets } from './currentDashboardSupportedTargets';
import airbnbLogoSrc from './images/Airbnb.svg';
import amazonLogoSrc from './images/Amazon.svg';
import appleAppStoreLogoSrc from './images/AppleAppStore.svg';
import autotraderLogoSrc from './images/Autotrader.svg';
import bbbLogoSrc from './images/BBB.svg';
import bingLogoSrc from './images/Bing.svg';
import googleLogoSrc from './images/Google.svg';
import instagramLogoSrc from './images/Instagram.svg';
import mobileDeLogoSrc from './images/MobileDE.svg';
import redditLogoSrc from './images/Reddit.svg';
import defaultLogoSrc from './images/Scraper.svg';
import targetIconSrc from './images/Target.svg';
import tiktokLogoSrc from './images/Tiktok.svg';
import walmartLogoSrc from './images/Walmart.svg';
import youTubeLogoSrc from './images/YouTube.svg';

type SvgImport = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const targetLogoMap: Record<TARGET_GROUP, SvgImport> = {
  google: googleLogoSrc,
  bing: bingLogoSrc,
  amazon: amazonLogoSrc,
  instagram: instagramLogoSrc,
  tiktok: tiktokLogoSrc,
  reddit: redditLogoSrc,
  ecommerce: defaultLogoSrc,
  walmart: walmartLogoSrc,
  target: targetIconSrc,
  youtube: youTubeLogoSrc,
  none: defaultLogoSrc,
  universal: defaultLogoSrc,
  lowes: defaultLogoSrc,
  kroger: defaultLogoSrc,
  ai: defaultLogoSrc,
  business_reviews: defaultLogoSrc,
  web_marketplace: defaultLogoSrc,
  web_travel: defaultLogoSrc,
};

const targetLogoOverrides: Partial<Record<TARGET, SvgImport>> = {
  [TARGET.GOOGLE_AI_MODE]: googleLogoSrc,
  [TARGET.BBB]: bbbLogoSrc,
  [TARGET.MOBILE]: mobileDeLogoSrc,
  [TARGET.APPLE_APP_STORE]: appleAppStoreLogoSrc,
  [TARGET.AIRBNB]: airbnbLogoSrc,
  [TARGET.AUTOTRADER]: autotraderLogoSrc,
};

export function getTargetLogo(target: Target) {
  const overriderLogo = targetLogoOverrides[target.targetKey];

  if (!!overriderLogo) {
    return targetLogoOverrides[target.targetKey];
  }

  return targetLogoMap[target.group] || defaultLogoSrc;
}

export type TargetLogo = keyof typeof targetLogoMap;

export type Target = {
  name: string;
  label: string;
  unavailable?: boolean;
  group: TARGET_GROUP;
  targetKey: TARGET;
  product: (typeof targetGroupLabels)[keyof typeof targetGroupLabels];
};

const availableTargetKeys: TARGET[] = [
  TARGET.GOOGLE_SEARCH,
  TARGET.UNIVERSAL,
  TARGET.AMAZON_SEARCH,
];

export const normalizedTargets: Target[] = Object.keys(sputilsTargetConfig)
  .filter((key) => currentDashboardSupportedTargets.includes(key as TARGET))
  .map((key) => {
    const targetKey = key as keyof typeof sputilsTargetConfig;
    const target = sputilsTargetConfig[targetKey as TARGET];

    return {
      name: targetKey,
      targetKey: targetKey,
      label: target.label?.id ?? '',
      unavailable: !availableTargetKeys.includes(targetKey),
      group: target.group,
      product: targetGroupLabels[target.group],
    };
  });

export const targets = normalizedTargets.toSorted((a, b) => {
  // available targets must follow list order from availableTargetKeys

  const aIndex = availableTargetKeys.indexOf(a.name as TARGET);
  const bIndex = availableTargetKeys.indexOf(b.name as TARGET);

  if (aIndex !== -1 && bIndex !== -1) {
    return aIndex - bIndex;
  }
  return 0;
});

export const getTargetByValue = (value: string): Target | null => {
  return targets.find((target) => target.name === value) || null;
};
